import CodeBlock, { CodeBlockOptions } from '@tiptap/extension-code-block';
import { Node as ProseMirrorNode } from 'prosemirror-model';

import { LowlightPlugin } from './lowlight-plugin';

import { MarkdownNodeSpec } from 'tiptap-markdown';

import { all, createLowlight } from 'lowlight';

import { NodeViewRenderer, ReactNodeViewRenderer } from '@tiptap/react';
import CodeBlockPreviewNodeView from './CodeBlockPreviewNodeView';
import CodeBlockPreviewWithExecutionNodeView from './CodeBlockPreviewWithExecutionNodeView';

const lowlightInstance = createLowlight(all);

export interface CodeBlockLowlightOptions extends CodeBlockOptions {
  enableCodeExecution?: boolean;
  defaultExpanded: boolean;
}

export type CodeBlockPreviewNodeT = typeof CodeBlockPreview;

/**
 * This extension allows you to highlight code blocks with lowlight.
 * @see https://tiptap.dev/api/nodes/code-block-lowlight
 */
const CodeBlockPreview = CodeBlock.extend<CodeBlockLowlightOptions>({
  addOptions() {
    return {
      ...this.parent?.(),
      enableCodeExecution: false,
      languageClassPrefix: 'language-',
      exitOnTripleEnter: true,
      exitOnArrowDown: true,
      defaultLanguage: null,
      defaultExpanded: true,
      HTMLAttributes: {
        class: 'hljs',
      },
    };
  },

  addStorage(): { markdown: MarkdownNodeSpec } {
    return {
      markdown: {
        serialize(state, node: ProseMirrorNode) {
          const code = node.textContent || '';

          state.write('```' + (node.attrs.language || ''));
          state.write('\n');
          state.text(code, false);
          state.ensureNewLine();
          state.write('```');
          state.closeBlock(node);
        },
        parse: {
          setup(markdownit) {
            markdownit.set({
              langPrefix: this.options.languageClassPrefix ?? 'language-',
            });
          },

          updateDOM(element) {
            element.innerHTML = element.innerHTML.replace(/\n<\/code><\/pre>/g, '</code></pre>');
          },
        },
      },
    };
  },

  addProseMirrorPlugins() {
    return [
      ...(this.parent?.() || []),
      LowlightPlugin({
        name: this.name,
        lowlight: lowlightInstance,
        defaultLanguage: this.options.defaultLanguage,
      }),
    ];
  },

  addNodeView(): NodeViewRenderer {
    if (this.options.enableCodeExecution) {
      return ReactNodeViewRenderer(CodeBlockPreviewWithExecutionNodeView);
    }
    return ReactNodeViewRenderer(CodeBlockPreviewNodeView);
  },
});

export default CodeBlockPreview;
